import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  Container,
  Grid,
  Button,
  Card,
  Collapse,
  CircularProgress,
  Alert,
  Tooltip,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarTodayIcon,
  DateRange as DateRangeIcon,
  GroupAdd as GroupAddIcon,
  History as HistoryIcon,
  SwapHoriz as SwapHorizIcon,
  DeleteOutline as DeleteOutlineIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import BudgetForm from '../components/BudgetForm';
import { colors } from '../styles';
import { getBudgetSpentRing, getBudgetSpentRingColor, getBudgetTotalSpent } from '../utils/budgetSpentRing';
import * as budgetService from '../services/budgetService';
import BudgetCategoryEditModal from '../components/BudgetCategoryEditModal';
import BudgetSelectorModal from '../components/BudgetSelectorModal';
// MonthSelector removed – budget periods drive insights now
import BudgetCategoryDetailsModal from '../components/BudgetCategoryDetailsModal';
import AddExpenseModal from '../components/AddExpenseModal';
import CountUp from 'react-countup';
import api, {
  getTransactions,
  getFamily,
  assignBudgetToFamilyMember,
  unassignBudgetFromFamilyMember,
  assignBudgetCategoryToFamilyMember,
  unassignBudgetCategoryFromFamilyMember,
  updateBudgetAccountMappings
} from '../services/api';
import BudgetCategoryMiniCard from '../components/BudgetCategoryMiniCard';
import AllowanceCard from '../components/AllowanceCard';
import { useAuth } from '../contexts/AuthContext';
import { useError } from '../contexts/ErrorContext';
import { CATEGORY_ICONS } from '../utils/categoryIcons';

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  borderRadius: '20px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  }
}));

// Removed legacy category icon/progress components (now using unified mini cards)

const StatCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '24px',
  backgroundColor: colors.white,
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(3),
  position: 'relative',
  overflow: 'hidden',
  border: `2px solid ${colors.primary}10`,
  transition: 'all 0.3s ease-in-out',
  minHeight: '160px',
  '&:hover': {
    transform: 'translateY(-4px)',
    border: `2px solid ${colors.primary}20`,
    '& .MuiSvgIcon-root': {
      transform: 'scale(1.1)'
    }
  }
}));

const StatIcon = styled(Box)(({ theme }) => ({
  width: 56,
  height: 56,
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: `${colors.primary}10`,
  transition: 'all 0.3s ease-in-out',
  '& .MuiSvgIcon-root': {
    fontSize: '28px',
    color: colors.primary,
    transition: 'all 0.3s ease-in-out'
  }
}));

const BudgetCard = styled(Card)(({ theme, active }) => ({
  borderRadius: '20px',
  padding: theme.spacing(2),
  cursor: 'pointer',
  backgroundColor: active ? `${colors.primary}10` : colors.white,
  border: active ? `2px solid ${colors.primary}` : '2px solid transparent',
  transition: 'all 0.2s ease-in-out',
  boxShadow: 'none',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: 'none'
  }
}));

const LockedBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 12,
  backgroundColor: `${colors.status.error}10`,
  color: colors.status.error,
  borderRadius: '8px',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  fontSize: '0.75rem',
  fontWeight: 700
}));

const CircularProgressWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '80px',
  height: '80px',
  marginRight: theme.spacing(2)
}));

const CircularProgressLabel = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column'
}));

// Removed unused CategoryCardCircle/Inner/Icon styled components

const Budgets = () => {
  const location = useLocation();
  const { hasFeature, user } = useAuth();
  const isChildUser = user?.family?.role === 'child';
  const { showSuccess } = useError();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [activeBudget, setActiveBudget] = useState(null);
  const [insights, setInsights] = useState(null);
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [autoGenerating, setAutoGenerating] = useState(false);
  
  // Check if background budget generation is in progress from onboarding
  const isBudgetGenerationInProgress = user?.onboarding?.transactionCategorizationProgress?.status === 'in_progress' || 
                                       user?.onboarding?.transactionCategorizationProgress?.status === 'starting';
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isCategoryEditModalOpen, setIsCategoryEditModalOpen] = useState(false);
  const [showBudgetSelector, setShowBudgetSelector] = useState(false);
  const [selectedCategoryForBudgetChoice, setSelectedCategoryForBudgetChoice] = useState(null);
  const [snapshotRange, setSnapshotRange] = useState('all');
  const [selectedCategoryForDetails, setSelectedCategoryForDetails] = useState(null);
  const [spendCategory, setSpendCategory] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [userHasToggledActiveBudget, setUserHasToggledActiveBudget] = useState(false);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [familyLoading, setFamilyLoading] = useState(false);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState({});
  const [categoryAuditLogs, setCategoryAuditLogs] = useState([]);
  const [categoryAuditLoading, setCategoryAuditLoading] = useState(false);
  const [categoryAuditExpanded, setCategoryAuditExpanded] = useState(true);
  const [categoryAuditRefreshKey, setCategoryAuditRefreshKey] = useState(0);
  const canManageFamilyAssignments = useMemo(
    () => user?.family?.role === 'primary' || user?.family?.role === 'spouse',
    [user]
  );
  /**
   * Snapshot data contains aggregated spending information for a selected range
   * { totalSpent: number, totalTransactions: number, period: { startDate, endDate }, categorySpent: { [categoryId]: number } }
   * Use this for quick widgets (today/week/month/year/all) and trend insights.
   */
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeMode, setCloseMode] = useState('auto'); // 'auto' | 'manual'
  const currentMonth = new Date().toISOString().slice(0, 7);

  // Always format currency to two decimals
  const formatCurrency = (value) => {
    const n = Number(value || 0);
    return n.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const fetchBudgets = async () => {
    try {
      setLoading(true);
      const response = await budgetService.getBudgets();
      setBudgets(response);
    } catch (err) {
      console.error('Error fetching budgets:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await getTransactions(1, 500, {});
      console.log('Transactions fetched:', response);
      if (response && response.transactions) {
        setTransactions(response.transactions);
      } else {
        console.warn('Unexpected response format for transactions:', response);
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]); // Clear transactions on error
    }
  };

  // Track only the specific budgetId, categoryId, and editMode query parameters
  // editMode is set when the user arrives from a budget action alert (Adjust/Allocate)
  // and unlocks a contextual banner explaining the action they were sent here to take.
  // This mirrors the focused single-budget edit experience that mobile gets via the
  // dedicated /budget-review-edit screen — see #279 for the platform-alignment context.
  const budgetIdParam = useMemo(() => new URLSearchParams(location.search).get('budgetId'), [location.search]);
  const categoryIdParam = useMemo(() => new URLSearchParams(location.search).get('categoryId'), [location.search]);
  const editModeParam = useMemo(() => new URLSearchParams(location.search).get('editMode'), [location.search]);
  const [budgetActionBannerDismissed, setBudgetActionBannerDismissed] = useState(false);

  // We intentionally do not depend on activeBudget/userHasToggledActiveBudget to prevent reload flicker
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Fetch budgets first
        const budgetResponse = await budgetService.getBudgets();
        setBudgets(budgetResponse);

        await fetchTransactions();

        // Fetch insights
        if (budgetResponse.length > 0) {
          const qsBudgetId = budgetIdParam;
          if (qsBudgetId) {
            const found = budgetResponse.find(b => String(b._id) === String(qsBudgetId));
            if (found) {
              setActiveBudget(found);
              const insightsResponse = await budgetService.getBudgetInsights(found._id);
              setInsights({ budgetInsights: { [found._id]: insightsResponse.data } });
            } else {
              const allInsights = { budgetInsights: {} };
              await Promise.all(budgetResponse.map(async (budget) => {
                const insightsResponse = await budgetService.getBudgetInsights(budget._id);
                allInsights.budgetInsights[budget._id] = insightsResponse.data;
              }));
              setInsights(allInsights);
            }
          } else {
            const allInsights = { budgetInsights: {} };
            await Promise.all(budgetResponse.map(async (budget) => {
              const insightsResponse = await budgetService.getBudgetInsights(budget._id);
              allInsights.budgetInsights[budget._id] = insightsResponse.data;
            }));
            setInsights(allInsights);
            // Only auto-select the single budget if the user hasn't explicitly toggled selection off
            if (budgetResponse.length === 1 && !activeBudget && !userHasToggledActiveBudget) {
              setActiveBudget(budgetResponse[0]);
            }
          }
        }

      } catch (err) {
        console.error('Error loading data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [budgetIdParam]);

  // Reset the budget-action banner dismissal whenever the editMode param changes,
  // so a new alert click re-surfaces the contextual help even if the previous banner was dismissed.
  useEffect(() => {
    setBudgetActionBannerDismissed(false);
  }, [editModeParam, budgetIdParam]);

  // When a categoryId deep link is present and we have an active budget, open details for that category
  useEffect(() => {
    const openDeepLinkedCategory = async () => {
      if (!activeBudget || !categoryIdParam) return;
      const cat = activeBudget.categories?.find(c => String(c._id) === String(categoryIdParam));
      if (!cat) return;
      try {
        const insightsResponse = await budgetService.getBudgetInsights(activeBudget._id);
        setInsights({ budgetInsights: { [activeBudget._id]: insightsResponse.data } });
      } catch {}
      setSelectedCategoryForDetails({ ...cat, budgetId: activeBudget._id });
    };
    openDeepLinkedCategory();
  }, [activeBudget, categoryIdParam]);

  // Fetch snapshot insights whenever range changes
  useEffect(() => {
    const fetchSnapshot = async () => {
      try {
        const res = await budgetService.getSnapshotInsights(snapshotRange);
        // Optionally store snapshot insights for display or future use
        // Here we merge into existing insights under a special key
        setInsights(prev => ({
          ...(prev || { budgetInsights: {} }),
          snapshot: res?.data || null
        }));
      } catch (e) {
        console.error('Failed to fetch snapshot insights', e);
      }
    };
    fetchSnapshot();
  }, [snapshotRange]);

  useEffect(() => {
    let mounted = true;
    if (!canManageFamilyAssignments) {
      setFamilyMembers([]);
      return () => {
        mounted = false;
      };
    }

    (async () => {
      try {
        setFamilyLoading(true);
        const response = await getFamily();
        if (!mounted) {
          return;
        }
        const members = response?.data?.family?.familyMembers || [];
        setFamilyMembers(members.filter((member) => member.status !== 'inactive'));
      } catch (err) {
        if (mounted) {
          // Silently ignore 402 premium required errors - user can still use budgets without family features
          // (mirrors the guard in mobile/app/(tabs)/budgets.tsx)
          if (err?.response?.status !== 402) {
            setError(err?.response?.data?.error || 'Unable to load family members');
          }
        }
      } finally {
        if (mounted) {
          setFamilyLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
  }, [canManageFamilyAssignments]);

  useEffect(() => {
    let mounted = true;

    const loadCategoryAuditLogs = async () => {
      if (!activeBudget?._id) {
        setCategoryAuditLogs([]);
        setCategoryAuditLoading(false);
        return;
      }

      try {
        setCategoryAuditLoading(true);
        const response = await budgetService.getBudgetCategoryAuditLogs(activeBudget._id);
        if (mounted) {
          setCategoryAuditLogs(response?.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch budget category audit logs', err);
        if (mounted) {
          setCategoryAuditLogs([]);
        }
      } finally {
        if (mounted) {
          setCategoryAuditLoading(false);
        }
      }
    };

    loadCategoryAuditLogs();

    return () => {
      mounted = false;
    };
  }, [activeBudget?._id, categoryAuditRefreshKey]);

  // removed month utilities

  const handleCreateBudget = async (budgetData) => {
    try {
      const response = await budgetService.createBudget(budgetData);
      setShowBudgetForm(false);
      await fetchBudgets();
      // Set as active budget and fetch insights
      setActiveBudget(response.data);
      const insightsResponse = await budgetService.getBudgetInsights(response.data._id);
      setInsights({ budgetInsights: { [response.data._id]: insightsResponse.data } });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Couldn't create budget. Check your amounts and try again.");
    }
  };

  const handleUpdateBudget = async (budgetData) => {
    try {
      const { accountMappings, ...rest } = budgetData || {};
      await budgetService.updateBudget(editingBudget._id, rest);
      if (Array.isArray(accountMappings)) {
        await updateBudgetAccountMappings(editingBudget._id, accountMappings);
      }
      setEditingBudget(null);
      await fetchBudgets();
      // Refresh insights if this is the active budget
      if (activeBudget?._id === editingBudget._id) {
        const insightsResponse = await budgetService.getBudgetInsights(editingBudget._id);
        setInsights({ budgetInsights: { [editingBudget._id]: insightsResponse.data } });
        try {
          const auditResponse = await budgetService.getBudgetCategoryAuditLogs(editingBudget._id);
          setCategoryAuditLogs(auditResponse?.data || []);
        } catch (auditErr) {
          console.error('Failed to refresh budget category audit logs after update', auditErr);
        }
        setCategoryAuditRefreshKey(key => key + 1);
      } else {
        // If no active budget, refresh all insights
        const allInsights = { budgetInsights: {} };
        await Promise.all(budgets.map(async (b) => {
          const insightsResponse = await budgetService.getBudgetInsights(b._id);
          allInsights.budgetInsights[b._id] = insightsResponse.data;
        }));
        setInsights(allInsights);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    try {
      await budgetService.deleteBudget(budgetId);
      setEditingBudget(null);
      // Clear insights if this was the active budget
      if (activeBudget?._id === budgetId) {
        setActiveBudget(null);
        setInsights(null);
      }
      await fetchBudgets();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Couldn't delete budget. Try again in a moment.");
    }
  };

  const handleAutoGenerateBudget = async () => {
    setAutoGenerating(true);
    try {
      // Trigger the full onboarding flow (transaction analysis + budget generation)
      // This ensures transactions are re-analyzed and budget is generated fresh
      await api.post('/onboarding/complete', { 
        step: 'accounts',
        isRetry: false 
      });
      // Refresh budgets to show the newly created budget
      await fetchBudgets();
      showSuccess('Budget generated successfully!');
    } catch (error) {
      console.error('Error auto-generating budget:', error);
      setError('Failed to generate budget automatically. Please try creating one manually.');
    } finally {
      setAutoGenerating(false);
    }
  };

  // Removed editor-driven category edit flow (legacy UI)
  /* const handleCategoryEditClick = (budget, category) => {
    // If a specific budget is provided, use it directly
    if (budget) {
      setSelectedCategory({ ...category, budgetId: budget._id });
      setIsCategoryEditModalOpen(true);
      return;
    }

    // For combined categories, check how many budgets have this category
    const budgetsWithCategory = budgets.filter(b =>
      b.categories?.some(c => c.name === category.name)
    );

    // If category exists in only one budget, edit it directly
    if (budgetsWithCategory.length === 1) {
      const budgetCategory = budgetsWithCategory[0].categories.find(c => c.name === category.name);
      setSelectedCategory({ ...budgetCategory, budgetId: budgetsWithCategory[0]._id });
      setIsCategoryEditModalOpen(true);
      return;
    }

    // Otherwise, show budget selector
    setSelectedCategoryForBudgetChoice(category);
    setShowBudgetSelector(true);
  }; */

  const handleBudgetSelect = (budget, budgetCategory) => {
    setShowBudgetSelector(false);
    setSelectedCategory({ ...budgetCategory, budgetId: budget._id });
    setIsCategoryEditModalOpen(true);
  };

  const handleCategoryUpdate = async (updatedCategory) => {
    try {
      if (selectedCategory.budgetId === 'all') {
        // Update the category in all relevant budgets
        await Promise.all(selectedCategory.budgetIds.map(budgetId =>
          budgetService.updateBudgetCategory(
            budgetId,
            selectedCategory._id,
            updatedCategory
          )
        ));
      } else {
        // Update in single budget
        await budgetService.updateBudgetCategory(
          selectedCategory.budgetId,
          selectedCategory._id,
          updatedCategory
        );
      }

      setIsCategoryEditModalOpen(false);
      // Refresh budgets
      await fetchBudgets();

      // Refresh insights
      const allInsights = { budgetInsights: {} };
      await Promise.all(budgets.map(async (budget) => {
        const insightsResponse = await budgetService.getBudgetInsights(budget._id);
        allInsights.budgetInsights[budget._id] = insightsResponse.data;
      }));
      setInsights(allInsights);
      setCategoryAuditRefreshKey((value) => value + 1);

    } catch (error) {
      console.error('Failed to update category:', error);
      setError(error.message);
    }
  };

  const getMemberLabel = (member) =>
    member?.name || member?.email || (member?.role === 'spouse' ? 'Spouse' : 'Child');

  const handleBudgetAssignmentChange = async (budgetId, memberId) => {
    if (!canManageFamilyAssignments) return;
    setAssignmentSaving((prev) => ({ ...prev, [budgetId]: true }));
    try {
      if (memberId) {
        await assignBudgetToFamilyMember(budgetId, memberId);
        showSuccess('Budget assigned to family member');
      } else {
        await unassignBudgetFromFamilyMember(budgetId);
        showSuccess('Budget assignment cleared');
      }
      await fetchBudgets();
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to update budget assignment');
    } finally {
      setAssignmentSaving((prev) => {
        const next = { ...prev };
        delete next[budgetId];
        return next;
      });
    }
  };

  const handleCategoryAssignmentChange = async (budgetId, categoryId, memberId) => {
    if (!canManageFamilyAssignments) return;
    const key = `${budgetId}:${categoryId}`;
    setAssignmentSaving((prev) => ({ ...prev, [key]: true }));
    try {
      if (memberId) {
        await assignBudgetCategoryToFamilyMember(budgetId, categoryId, memberId);
        showSuccess('Category assigned to family member');
      } else {
        await unassignBudgetCategoryFromFamilyMember(budgetId, categoryId);
        showSuccess('Category assignment cleared');
      }
      await fetchBudgets();
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to update category assignment');
    } finally {
      setAssignmentSaving((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  /* const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    if (name.includes('home') || name.includes('rent') || name.includes('mortgage')) return <HomeIcon />;
    if (name.includes('car') || name.includes('transport')) return <CarIcon />;
    if (name.includes('food') || name.includes('grocery') || name.includes('restaurant')) return <FoodIcon />;
    if (name.includes('health') || name.includes('medical')) return <HealthIcon />;
    if (name.includes('shopping')) return <ShoppingCartIcon />;
    if (name.includes('education') || name.includes('school')) return <EducationIcon />;
    if (name.includes('entertainment') || name.includes('fun')) return <EntertainmentIcon />;
    if (name.includes('utilities') || name.includes('bills')) return <UtilitiesIcon />;
    if (name.includes('savings') || name.includes('investment')) return <SavingsIcon />;
    return <OtherIcon />;
  }; */

  const handleCategoryClick = async (budget, category) => {
    try {
      const targetBudget = budget || activeBudget;
      if (!targetBudget) return;
      setSelectedCategoryForDetails({
        ...category,
        budgetId: targetBudget._id
      });
      const insightsResponse = await budgetService.getBudgetInsights(targetBudget._id);
      setInsights({ budgetInsights: { [targetBudget._id]: insightsResponse.data } });
    } catch (err) {
      console.error('Error fetching category insights:', err);
      setError(err.message);
    }
  };

  // Removed renderCategoryCard in favor of unified mini card rendering

  const getFilteredInsights = () => insights;

  const getAllCategories = () => {
    if (!budgets || !Array.isArray(budgets)) {
      return [];
    }
    
    const allCategories = [];
    const seenNames = new Set();
    const filteredInsights = getFilteredInsights();
      
    budgets.forEach(budget => {
      if (!budget.categories) return;
      budget.categories.forEach(category => {
        if (!seenNames.has(category.name)) {
          seenNames.add(category.name);
          
          const categoryData = budgets.reduce((acc, b) => {
            const matchingCategory = b.categories?.find(c => c.name === category.name);
            if (matchingCategory) {
              const amount = matchingCategory.isPercentage 
                ? ((Number(matchingCategory.amount) / 100) * Number(b.totalBudget))
                : Number(matchingCategory.amount);
              acc.totalAmount += amount;
              
              const budgetInsights = filteredInsights?.budgetInsights?.[b._id];
              const spent = budgetInsights?.categorySpent?.[matchingCategory._id] || 0;
              acc.totalSpent += spent;
            }
            return acc;
          }, { totalAmount: 0, totalSpent: 0 });

          allCategories.push({
            ...category,
            totalAmount: categoryData.totalAmount,
            totalSpent: categoryData.totalSpent,
            budgetCount: budgets.filter(b => 
              b.categories?.some(c => c.name === category.name)
            ).length
          });
        }
      });
    });

    return allCategories.sort((a, b) => b.totalAmount - a.totalAmount);
  };

  // Removed unused getTotalStats after UI change to mini cards

  const handleBudgetClick = async (budget) => {
    try {
      if (activeBudget?._id === budget._id) {
        // If clicking the same budget, deselect it and show all budgets' insights
        setActiveBudget(null);
        setUserHasToggledActiveBudget(true);
        const allInsights = { budgetInsights: {} };
        await Promise.all(budgets.map(async (b) => {
          const insightsResponse = await budgetService.getBudgetInsights(b._id);
          allInsights.budgetInsights[b._id] = insightsResponse.data;
        }));
        setInsights(allInsights);
      } else {
        // If selecting a different budget, update active budget and get its insights
        setActiveBudget(budget);
        // Reset manual toggle since the user explicitly selected a budget
        setUserHasToggledActiveBudget(false);
        const insightsResponse = await budgetService.getBudgetInsights(budget._id);
        setInsights({ budgetInsights: { [budget._id]: insightsResponse.data } });
      }
    } catch (err) {
      console.error('Error updating budget insights:', err);
      setError(err.message);
    }
  };

  const getTotalSpentForRange = (transactions, startDate, endDate) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const filteredTransactions = transactions.filter(t => {
      const txDate = new Date(t.authorizedDate || t.date);
      // Use canonical type field, with fallback to legacy transactionType for backward compatibility
      const txType = t.type || t.transactionType;
      // Include expenses and refunds/reimbursements (which reduce spending)
      const isExpenseOrRefund = txType === 'expense' || 
        (txType === 'income' && ['refund', 'reimbursement'].includes(t.subtype));
      return txDate >= startDate && txDate <= endDate && isExpenseOrRefund;
    });
    
    return filteredTransactions.reduce((total, t) => total + t.amount, 0);
  };

  const getTotalSpentToday = (transactions) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    return getTotalSpentForRange(transactions, today, tomorrow);
  };

  const getTotalSpentThisWeek = (transactions) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + (6 - today.getDay()));
    
    return getTotalSpentForRange(transactions, startOfWeek, endOfWeek);
  };

  const getTotalSpentThisMonth = (transactions, month = null) => {
    if (!transactions || !Array.isArray(transactions)) return 0;
    
    const now = new Date();
    let firstDayOfMonth, lastDayOfMonth;
    
    if (month) {
      const [year, monthNum] = month.split('-').map(Number);
      firstDayOfMonth = new Date(year, monthNum - 1, 1);
      lastDayOfMonth = new Date(year, monthNum, 0); // Last day of the month
    } else {
      firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    
    return getTotalSpentForRange(transactions, firstDayOfMonth, lastDayOfMonth);
  };

  const getBudgetRemaining = (budget) => {
    if (!budget) return 0;
    const allocated = (budget.categories || []).reduce((sum, cat) => {
      const amount = cat.isPercentage
        ? (Number(cat.amount || 0) / 100) * Number(budget.totalBudget || 0)
        : Number(cat.amount || 0);
      return sum + amount;
    }, 0);
    return Number(budget.totalBudget || 0) - allocated;
  };

  const getAuditActionLabel = (log) => {
    const source = log.sourceCategoryName || 'Category';
    const target = log.targetCategoryName || 'another category';

    if (log.action === 'reallocate') {
      return `${source} moved to ${target}`;
    }
    if (log.action === 'delete-with-reassign') {
      return `${source} deleted, moved to ${target}`;
    }
    return `${source} deleted, transactions uncategorized`;
  };

  const getAuditActionIcon = (action) => (
    action === 'reallocate'
      ? <SwapHorizIcon fontSize="small" />
      : <DeleteOutlineIcon fontSize="small" />
  );

  const getAuditDetail = (log) => {
    const count = Number(log.totalTransactionsUpdated || 0);
    const transactionLabel = count === 1 ? 'transaction' : 'transactions';
    const atomicLabel = log.atomic ? 'Atomic update' : 'Sequential update';
    return `${count} ${transactionLabel} updated. ${atomicLabel}.`;
  };

  const formatAuditTimestamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  // Only surface the banner while the active budget still matches the one the alert pointed at.
  // Requires both a budgetId URL param and an activeBudget whose _id matches it — otherwise the
  // banner copy (which names the budget) would imply a target that wasn't actually picked by the
  // alert. If the user manually toggles to a different budget the banner hides for the same reason.
  // Locked budgets also suppress the banner — its copy ("Adjust the categories below") would be
  // misleading because all edit controls are disabled. Mirrors the mobile guard at
  // mobile/app/budget-review-edit.tsx.
  const showBudgetActionBanner =
    !budgetActionBannerDismissed
    && !!editModeParam
    && (editModeParam === 'allocate' || editModeParam === 'adjust')
    && !!budgetIdParam
    && !!activeBudget
    && String(activeBudget._id) === String(budgetIdParam)
    && !activeBudget.locked;

  const budgetActionBannerCopy = editModeParam === 'allocate'
    ? {
      severity: 'info',
      title: 'Allocate your unallocated funds',
      message: `${activeBudget?.name || 'This budget'} has unallocated funds. Adjust the categories below so every dollar is assigned a job.`
    }
    : {
      severity: 'warning',
      title: 'Reduce category amounts',
      message: `${activeBudget?.name || 'This budget'} is over-allocated. Lower some category amounts so the totals match the budget.`
    };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ p: { xs: 3, sm: 6, md: 8 } }}>
        {/* Budget period insights are driven by each budget's start/end dates */}

        {showBudgetActionBanner ? (
          <Alert
            severity={budgetActionBannerCopy.severity}
            onClose={() => setBudgetActionBannerDismissed(true)}
            sx={{ mb: 3, borderRadius: '12px' }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {budgetActionBannerCopy.title}
            </Typography>
            <Typography variant="body2">{budgetActionBannerCopy.message}</Typography>
          </Alert>
        ) : null}

        {/* Spending Overview */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <StatIcon>
                  <CalendarTodayIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Spent Today
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalSpentToday(transactions)}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <StatIcon>
                  <DateRangeIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Spent This Week
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalSpentThisWeek(transactions)}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard>
                <StatIcon>
                  <TrendingUpIcon />
                </StatIcon>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 1,
                      fontSize: '0.875rem',
                      letterSpacing: '0.1px'
                    }}
                  >
                    Spent This Month
                  </Typography>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 700,
                      color: colors.primary,
                      letterSpacing: '-0.5px',
                      mb: 0.5
                    }}
                  >
                    $<CountUp
                      end={getTotalSpentThisMonth(transactions, currentMonth)}
                      decimals={2}
                      duration={1}
                      separator=","
                      preserveValue={true}
                    />
                  </Typography>
                </Box>
              </StatCard>
            </Grid>
          </Grid>
          {/* Snapshot range chips - always visible */}
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={1}>
              {[
                { key: 'today', label: 'Today' },
                { key: 'week', label: 'This Week' },
                { key: 'month', label: 'This Month' },
                { key: 'year', label: 'This Year' },
                { key: 'all', label: 'All' }
              ].map(opt => (
                <Chip
                  key={opt.key}
                  label={opt.label}
                  color={snapshotRange === opt.key ? 'primary' : 'default'}
                  variant={snapshotRange === opt.key ? 'filled' : 'outlined'}
                  onClick={() => setSnapshotRange(opt.key)}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        {!isChildUser ? (
          <Box sx={{ mb: 4 }}>
            <AllowanceCard
              refreshKey={categoryAuditRefreshKey}
              onConfigure={() => {
                const budget = activeBudget || budgets.find((item) => item.isActive !== false && !item.locked);
                const category = budget?.categories?.[0];
                if (budget && category) {
                  setSelectedCategory({ ...category, budgetId: budget._id });
                  setIsCategoryEditModalOpen(true);
                } else {
                  setEditingBudget(null);
                  setShowBudgetForm(true);
                }
              }}
            />
          </Box>
        ) : null}

        <Box sx={{ py: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Budget Stats section removed to show category mini cards in the main active view below */}

          {/* Budget Selection */}
          {Array.isArray(budgets) && budgets.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Your Budgets
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {canManageFamilyAssignments && (
                    <Button
                      variant="outlined"
                      startIcon={<GroupAddIcon />}
                      onClick={() => setAssignmentDialogOpen(true)}
                    >
                      Manage family assignments
                    </Button>
                  )}
                  {activeBudget && (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => { if (!activeBudget.locked) { setEditingBudget(activeBudget); setShowBudgetForm(true); } }}
                        disabled={!!activeBudget.locked}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => { if (!activeBudget.locked) { setCloseMode('auto'); setShowCloseDialog(true); } }}
                        disabled={!!activeBudget.locked}
                      >
                        Close
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        disabled={!!activeBudget.locked || !!activeBudget.approvedAt || Math.abs(getBudgetRemaining(activeBudget)) > 0.01}
                        onClick={async () => {
                          try {
                            if (!activeBudget || Math.abs(getBudgetRemaining(activeBudget)) > 0.01) return;
                            await budgetService.approveBudget(activeBudget._id);
                            // Refresh budgets and active budget state
                            const refreshed = await budgetService.getBudgets();
                            setBudgets(refreshed);
                            const updatedActive = refreshed.find(b => String(b._id) === String(activeBudget._id));
                            if (updatedActive) setActiveBudget(updatedActive);
                          } catch (e) {
                            console.error('Failed to approve budget', e);
                          }
                        }}
                      >
                        Approve
                      </Button>
                    </>
                  )}
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { setEditingBudget(null); setShowBudgetForm(true); }}
                    sx={{
                      backgroundColor: colors.primary,
                      '&:hover': {
                        backgroundColor: colors.primaryDark
                      }
                    }}
                  >
                    Create Budget
                  </Button>
                </Box>
              </Box>
              <Grid container spacing={2}>
                {budgets.map((budget) => {
                  const spentRing = getBudgetSpentRing({
                    totalBudget: budget.totalBudget,
                    totalSpent: getBudgetTotalSpent(insights?.budgetInsights?.[budget._id]),
                  });
                  const spentRingColor = getBudgetSpentRingColor(spentRing.isOverBudget, colors);
                  return (
                    <Grid item xs={12} sm={6} md={4} key={budget._id}>
                      <BudgetCard
                        active={activeBudget?._id === budget._id}
                        onClick={() => handleBudgetClick(budget)}
                      >
                        {budget.locked && (
                          <LockedBadge>
                            Locked
                          </LockedBadge>
                        )}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              {budget.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {budget.period.charAt(0).toUpperCase() + budget.period.slice(1)} Budget
                            </Typography>
                            <Typography variant="h6" sx={{ mt: 1, color: colors.primary }}>
                              {formatCurrency(budget.totalBudget)}
                            </Typography>
                          </Box>
                          <Tooltip
                            title={spentRing.tooltip}
                            arrow
                            placement="left"
                          >
                            <Box sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              backgroundColor: `${spentRingColor}10`,
                              borderRadius: '12px',
                              padding: '16px',
                              minWidth: '120px'
                            }}>
                              <CircularProgressWrapper sx={{ marginRight: 0, marginBottom: 1 }}>
                                <CircularProgress
                                  variant="determinate"
                                  value={spentRing.clampedPercentage}
                                  size={80}
                                  thickness={4}
                                  sx={{
                                    color: spentRingColor,
                                    '& .MuiCircularProgress-circle': {
                                      strokeLinecap: 'round'
                                    }
                                  }}
                                />
                                <CircularProgressLabel>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: spentRingColor,
                                      fontWeight: 600,
                                      fontSize: '1.1rem'
                                    }}
                                  >
                                    {spentRing.labelPercentage.toFixed(0)}%
                                  </Typography>
                                </CircularProgressLabel>
                              </CircularProgressWrapper>
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: spentRingColor,
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    mb: 0.5
                                  }}
                                >
                                  {spentRing.message}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: spentRingColor,
                                    opacity: 0.8,
                                    fontSize: '0.875rem'
                                  }}
                                >
                                  {spentRing.subMessage}
                                </Typography>
                              </Box>
                            </Box>
                          </Tooltip>
                        </Box>
                      </BudgetCard>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Active Budget View or All Categories */}
          {activeBudget ? (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                Budget Categories
              </Typography>
              <Grid container spacing={4}>
                {(activeBudget.categories || []).map((category) => {
                  const filteredInsights = getFilteredInsights();
                  const spent = filteredInsights?.budgetInsights?.[activeBudget._id]?.categorySpent?.[category._id] || 0;
                  const budgeted = category.isPercentage
                    ? ((Number(category.amount || 0) / 100) * Number(activeBudget.totalBudget || 0))
                    : Number(category.amount || 0);
                  return (
                    <Grid item xs={12} sm={6} md={3} lg={3} key={category._id}>
                      <StyledCard sx={{ boxShadow: 'none', cursor: 'pointer' }} onClick={() => handleCategoryClick(activeBudget, category)}>
                        <BudgetCategoryMiniCard
                          name={category.name}
                          budgeted={budgeted}
                          spent={spent}
                          icon={CATEGORY_ICONS[category.icon] || CATEGORY_ICONS.other}
                          color={category.color}
                        />
                      </StyledCard>
                    </Grid>
                  );
                })}
              </Grid>
              <Box sx={{ mt: 4 }}>
                <Button
                  variant="text"
                  startIcon={<HistoryIcon />}
                  endIcon={categoryAuditExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setCategoryAuditExpanded((expanded) => !expanded)}
                  aria-expanded={categoryAuditExpanded}
                  sx={{
                    px: 0,
                    color: colors.secondary,
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: 'transparent', color: colors.primary }
                  }}
                >
                  Recent category changes
                </Button>
                <Collapse in={categoryAuditExpanded}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      borderRadius: '16px',
                      backgroundColor: colors.white,
                      border: `1px solid ${colors.primary}18`
                    }}
                  >
                    {categoryAuditLoading ? (
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          Loading recent changes
                        </Typography>
                      </Stack>
                    ) : categoryAuditLogs.length ? (
                      <Stack spacing={1.25}>
                        {categoryAuditLogs.map((log, index) => (
                          <Box
                            key={log._id || `${log.action}-${index}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              pb: index === categoryAuditLogs.length - 1 ? 0 : 1.25,
                              borderBottom: index === categoryAuditLogs.length - 1
                                ? 'none'
                                : `1px solid ${colors.primary}12`
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                flex: '0 0 32px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: colors.primary,
                                backgroundColor: `${colors.primary}12`
                              }}
                            >
                              {getAuditActionIcon(log.action)}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: colors.secondary }}>
                                {getAuditActionLabel(log)}
                              </Typography>
                              <Typography variant="caption" sx={{ color: colors.text.secondary, display: 'block' }}>
                                {getAuditDetail(log)}
                              </Typography>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{ color: colors.text.secondary, flexShrink: 0, whiteSpace: 'nowrap' }}
                            >
                              {formatAuditTimestamp(log.createdAt)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No bulk category changes yet.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>
            </>
          ) : Array.isArray(budgets) && budgets.length > 0 ? (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                All Categories
              </Typography>
              <Grid container spacing={4}>
                {getAllCategories().map((category) => (
                  <Grid item xs={12} sm={6} md={3} lg={3} key={category._id}>
                    <StyledCard sx={{ boxShadow: 'none' }}>
                      <BudgetCategoryMiniCard
                        name={category.name}
                        budgeted={category.totalAmount}
                        spent={category.totalSpent}
                        icon={CATEGORY_ICONS[category.icon] || CATEGORY_ICONS.other}
                        color={category.color}
                      />
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Box sx={{
              textAlign: 'center',
              py: 8,
              backgroundColor: `${colors.primary}05`,
              borderRadius: '20px'
            }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                No active budgets
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {isBudgetGenerationInProgress 
                  ? `We're analyzing your transactions and creating your personalized budget. This may take a few moments...`
                  : hasFeature('ai_insights')
                    ? `Let's create a personalized budget based on your spending patterns`
                    : `Create your first budget to start tracking your expenses`
                }
              </Typography>
              {isBudgetGenerationInProgress ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Creating your budget...
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2} direction="row" justifyContent="center">
                  {hasFeature('ai_insights') && (
                    <Button
                      variant="contained"
                      startIcon={autoGenerating ? <CircularProgress size={16} /> : <TrendingUpIcon />}
                      onClick={handleAutoGenerateBudget}
                      disabled={autoGenerating}
                      sx={{ minWidth: 180 }}
                    >
                      {autoGenerating ? 'Creating...' : 'Auto-Generate Budget'}
                    </Button>
                  )}
                  <Button
                    variant={hasFeature('ai_insights') ? 'outlined' : 'contained'}
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setEditingBudget(null);
                      setShowBudgetForm(true);
                    }}
                    disabled={autoGenerating || isBudgetGenerationInProgress}
                  >
                    Create Manually
                  </Button>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Container>

      <BudgetForm
        open={showBudgetForm || !!editingBudget}
        onClose={() => {
          setShowBudgetForm(false);
          setEditingBudget(null);
        }}
        onSubmit={editingBudget ? handleUpdateBudget : handleCreateBudget}
        onDelete={handleDeleteBudget}
        initialData={editingBudget}
      />

      <BudgetSelectorModal
        open={showBudgetSelector}
        onClose={() => {
          setShowBudgetSelector(false);
          setSelectedCategoryForBudgetChoice(null);
        }}
        budgets={budgets}
        category={selectedCategoryForBudgetChoice}
        onBudgetSelect={handleBudgetSelect}
      />

      {selectedCategory && (
        <BudgetCategoryEditModal
          open={isCategoryEditModalOpen}
          onClose={() => setIsCategoryEditModalOpen(false)}
          category={selectedCategory}
          onSave={handleCategoryUpdate}
        />
      )}

      {selectedCategoryForDetails && (
        <BudgetCategoryDetailsModal
          open={!!selectedCategoryForDetails}
          onClose={() => setSelectedCategoryForDetails(null)}
          category={selectedCategoryForDetails}
          budget={activeBudget}
          insights={insights}
          budgets={budgets}
          onReallocated={async () => {
            // Refresh insights so the modal's transactions list and the parent page
            // reflect the new category assignments after a bulk reallocation.
            if (!activeBudget?._id) return;
            try {
              const insightsResponse = await budgetService.getBudgetInsights(activeBudget._id);
              const auditResponse = await budgetService.getBudgetCategoryAuditLogs(activeBudget._id);
              setInsights(prev => ({
                ...(prev || {}),
                budgetInsights: {
                  ...(prev?.budgetInsights || {}),
                  [activeBudget._id]: insightsResponse.data
                }
              }));
              setCategoryAuditLogs(auditResponse?.data || []);
            } catch (e) {
              console.error('Failed to refresh insights after reallocation', e);
            }
          }}
          onSpend={(category) => setSpendCategory(category)}
          onAssigned={async (result) => {
            const allocated = result?.data?.target?.allocated;
            if (allocated != null) {
              setSelectedCategoryForDetails((prev) => (
                prev ? { ...prev, amount: allocated, isPercentage: false } : prev
              ));
            }
            await fetchBudgets();
            if (!activeBudget?._id) return;
            try {
              const insightsResponse = await budgetService.getBudgetInsights(activeBudget._id);
              setInsights(prev => ({
                ...(prev || {}),
                budgetInsights: {
                  ...(prev?.budgetInsights || {}),
                  [activeBudget._id]: insightsResponse.data
                }
              }));
            } catch (e) {
              console.error('Failed to refresh insights after assign', e);
            }
          }}
        />
      )}

      <AddExpenseModal
        open={Boolean(spendCategory)}
        initialCategoryId={spendCategory?._id}
        initialBudgetId={spendCategory?.budgetId || activeBudget?._id}
        onClose={() => setSpendCategory(null)}
        onSuccess={() => {
          setSpendCategory(null);
          fetchBudgets();
        }}
      />

      {canManageFamilyAssignments && (
        <Dialog
          open={assignmentDialogOpen}
          onClose={() => setAssignmentDialogOpen(false)}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Manage family assignments</DialogTitle>
          <DialogContent dividers>
            {familyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : familyMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Invite family members to assign budgets and categories.
              </Typography>
            ) : (
              <Stack spacing={3}>
                {budgets.map((budget) => {
                  const budgetAssignmentValue =
                    budget.metadata?.familyMemberId?.toString() ||
                    budget.linkedUserId?.toString() ||
                    '';
                  return (
                    <Box
                      key={budget._id}
                      sx={{
                        border: 1,
                        borderColor: colors.border,
                        borderRadius: 3,
                        p: 2
                      }}
                    >
                      <Stack spacing={1}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          alignItems={{ xs: 'flex-start', md: 'center' }}
                          justifyContent="space-between"
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              {budget.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatCurrency(budget.totalBudget)} · {budget.period}
                            </Typography>
                          </Box>
                          <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel id={`budget-assignment-${budget._id}`}>Budget assignment</InputLabel>
                            <Select
                              labelId={`budget-assignment-${budget._id}`}
                              label="Budget assignment"
                              value={budgetAssignmentValue}
                              onChange={(event) =>
                                handleBudgetAssignmentChange(budget._id, event.target.value || null)
                              }
                              disabled={!!assignmentSaving[budget._id]}
                            >
                              <MenuItem value="">
                                <em>No assignment</em>
                              </MenuItem>
                              {familyMembers.map((member) => (
                                <MenuItem key={member._id} value={member._id}>
                                  {getMemberLabel(member)}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Stack>

                        {(budget.categories || []).length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              Categories
                            </Typography>
                            <Stack spacing={1.5}>
                              {budget.categories.map((category) => {
                                const categoryKey = `${budget._id}:${category._id}`;
                                const categoryValue = category.familyMemberId
                                  ? String(category.familyMemberId)
                                  : '';
                                return (
                                  <Stack
                                    key={category._id}
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={1}
                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                    justifyContent="space-between"
                                  >
                                    <Typography variant="body2">{category.name}</Typography>
                                    <FormControl size="small" sx={{ minWidth: 200 }}>
                                      <InputLabel id={`category-assignment-${category._id}`}>
                                        Assign to
                                      </InputLabel>
                                      <Select
                                        labelId={`category-assignment-${category._id}`}
                                        label="Assign to"
                                        value={categoryValue}
                                        onChange={(event) =>
                                          handleCategoryAssignmentChange(
                                            budget._id,
                                            category._id,
                                            event.target.value || null
                                          )
                                        }
                                        disabled={!!assignmentSaving[categoryKey]}
                                      >
                                        <MenuItem value="">
                                          <em>No assignment</em>
                                        </MenuItem>
                                        {familyMembers.map((member) => (
                                          <MenuItem key={member._id} value={member._id}>
                                            {getMemberLabel(member)}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Stack>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssignmentDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Close Budget dialog */}
      <Dialog open={showCloseDialog} onClose={() => setShowCloseDialog(false)}>
        <DialogTitle>Close Budget</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            How would you like to create your next budget?
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip
              label="AI: Create for me"
              color={closeMode === 'auto' ? 'primary' : 'default'}
              variant={closeMode === 'auto' ? 'filled' : 'outlined'}
              onClick={() => setCloseMode('auto')}
            />
            <Chip
              label="I'll create it manually"
              color={closeMode === 'manual' ? 'primary' : 'default'}
              variant={closeMode === 'manual' ? 'filled' : 'outlined'}
              onClick={() => setCloseMode('manual')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCloseDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              try {
                if (!activeBudget) return;
                const res = await budgetService.closeBudget(activeBudget._id, closeMode);
                setShowCloseDialog(false);
                await fetchBudgets();
                // If next budget was auto-created, make it active
                if (res?.data?.nextBudget) {
                  setActiveBudget(res.data.nextBudget);
                } else {
                  setActiveBudget(null);
                }
              } catch (e) {
                console.error('Failed to close budget', e);
              }
            }}
          >
            Close Budget
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Budgets;
